import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  EmojiEvents,
  People,
  CalendarMonth,
  School,
  FitnessCenter,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
} from '@mui/icons-material';

export const HomePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // If already logged in, redirect to dashboard
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: <People sx={{ fontSize: 60 }} />,
      title: 'Expert Coaching',
      description: 'Train with qualified LTA coaches who are passionate about developing players of all ages and abilities.',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 60 }} />,
      title: 'Structured Programs',
      description: 'Join our organized squad sessions designed for different age groups and skill levels, from beginners to elite players.',
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 60 }} />,
      title: 'Competition Support',
      description: 'Participate in tournaments and receive guidance to help you achieve your competitive goals.',
    },
    {
      icon: <School sx={{ fontSize: 60 }} />,
      title: 'Tennis Education',
      description: 'Access comprehensive resources to improve your game, understand tactics, and develop tennis IQ.',
    },
    {
      icon: <FitnessCenter sx={{ fontSize: 60 }} />,
      title: 'Fitness Training',
      description: 'Benefit from specialized strength and conditioning programs designed to enhance your athletic performance.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ bgcolor: '#1a1a1a' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            🎾 GKM Tennis Academy
          </Typography>
          <Button
            color="inherit"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{ mr: 1 }}
          >
            Login
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RegisterIcon />}
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: 'white',
              color: '#1a1a1a',
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            Register
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -50,
            top: -50,
            fontSize: '300px',
            opacity: 0.05,
          }}
        >
          🎾
        </Box>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Welcome to GKM Tennis Academy
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.9,
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Where passion meets excellence. Join our tennis academy and experience world-class coaching,
                professional training programs, and a supportive community dedicated to developing champions.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<RegisterIcon />}
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: 'white',
                    color: '#1a1a1a',
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  Login
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '200px',
                }}
              >
                🎾
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{ fontWeight: 700, color: '#1a1a1a' }}
          >
            Why Choose GKM Tennis Academy?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Everything you need to excel in tennis, from professional coaching to competitive opportunities
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box
                    sx={{
                      color: '#1a1a1a',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight={600}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Call to Action Section */}
      <Box
        sx={{
          bgcolor: '#1a1a1a',
          color: 'white',
          py: 8,
          mt: 8,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Start Your Tennis Journey?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Join GKM Tennis Academy and discover your potential on court
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<RegisterIcon />}
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: 'white',
              color: '#1a1a1a',
              py: 1.5,
              px: 5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            Create Your Account
          </Button>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: '#000000',
          color: 'white',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            © {new Date().getFullYear()} GKM Tennis Academy. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
